import { GraphQLClient } from 'graphql-request';
import { gql } from 'urql';
import type { Mutation, Query } from '../generated/graphql';
import type {
  SaveEstateStatusChecklistInput,
  SaveEstateStatusFirmTemplateInput,
  EstateStatusReadOnlyHouseholdSettings,
} from '../EstateStatus/types';
import { ErrorCode, ErrorCodeError } from './errors';

const EstateStatusChecklistSelection = `
  changeToken
  score
  summary {
    completed
    applicable
    total
    earnedWeight
    applicableWeight
    totalWeight
    score
  }
  checklist {
    id
    label
    weight
    state
    lastReviewedAt
    note
    attachmentLinks {
      id
      fileName
      url
      uploadedAt
      uploadedByUserID
    }
  }
`;

const EstateStatusFirmTemplateSelection = `
  firmID
  changeToken
  checklist {
    id
    label
    weight
  }
`;

const EstateStatusReadOnlyChecklistSelection = `
  changeToken
  score
  summary {
    completed
    applicable
    total
    earnedWeight
    applicableWeight
    totalWeight
    score
  }
  checklist {
    id
    label
    weight
    state
    lastReviewedAt
  }
`;

const estateStatusQuery = gql`
  query EstateStatus($householdID: ID!) {
    estateStatus(householdID: $householdID) {
      ${EstateStatusChecklistSelection}
    }
  }
`;

const estateStatusFirmTemplateQuery = gql`
  query EstateStatusFirmTemplate($firmID: ID!) {
    estateStatusFirmTemplate(firmID: $firmID) {
      ${EstateStatusFirmTemplateSelection}
    }
  }
`;

export const estateStatusReadOnlyQuery = gql`
  query EstateStatusReadOnly($householdID: ID!) {
    estateStatus(householdID: $householdID) {
      ${EstateStatusReadOnlyChecklistSelection}
    }
  }
`;

const saveEstateStatusMutation = gql`
  mutation SaveEstateStatus($input: SaveEstateStatusInput!) {
    saveEstateStatus(input: $input) {
      estateStatus {
        ${EstateStatusChecklistSelection}
      }
    }
  }
`;

const saveEstateStatusFirmTemplateMutation = gql`
  mutation SaveEstateStatusFirmTemplate($input: SaveEstateStatusFirmTemplateInput!) {
    saveEstateStatusFirmTemplate(input: $input) {
      template {
        ${EstateStatusFirmTemplateSelection}
      }
    }
  }
`;

export const clientGetEstateStatusHouseholdSettings = async (client: GraphQLClient, args: { householdID: string }) => {
  const response = await client.request<Pick<Query, 'estateStatus'>>(estateStatusQuery, args);

  if (!response.estateStatus) {
    throw new Error('Estate Status household settings were not returned.');
  }

  return response.estateStatus;
};

export const clientGetEstateStatusReadOnlyHouseholdSettings = async (
  client: GraphQLClient,
  args: { householdID: string }
) => {
  const response = await client.request<{ estateStatus: EstateStatusReadOnlyHouseholdSettings | null }>(
    estateStatusReadOnlyQuery,
    args
  );

  if (!response.estateStatus) {
    throw new ErrorCodeError(ErrorCode.ItemNotFound);
  }

  return response.estateStatus;
};

export const clientGetEstateStatusFirmTemplate = async (client: GraphQLClient, args: { firmID: string }) => {
  const response = await client.request<Pick<Query, 'estateStatusFirmTemplate'>>(estateStatusFirmTemplateQuery, args);

  if (!response.estateStatusFirmTemplate) {
    throw new Error('Estate Status firm template was not returned.');
  }

  return response.estateStatusFirmTemplate;
};

export const clientSaveEstateStatusHouseholdSettings = async (
  client: GraphQLClient,
  input: { householdID: string } & SaveEstateStatusChecklistInput
) => {
  const response = await client.request<Pick<Mutation, 'saveEstateStatus'>>(saveEstateStatusMutation, { input });

  if (!response.saveEstateStatus?.estateStatus) {
    throw new Error('SaveEstateStatus response did not include estate status settings.');
  }

  return response.saveEstateStatus.estateStatus;
};

export const clientSaveEstateStatusFirmTemplate = async (
  client: GraphQLClient,
  input: { firmID: string } & SaveEstateStatusFirmTemplateInput
) => {
  const response = await client.request<Pick<Mutation, 'saveEstateStatusFirmTemplate'>>(
    saveEstateStatusFirmTemplateMutation,
    { input }
  );

  if (!response.saveEstateStatusFirmTemplate?.template) {
    throw new Error('SaveEstateStatusFirmTemplate response did not include a firm template.');
  }

  return response.saveEstateStatusFirmTemplate.template;
};
